import { Client, Functions } from "appwrite";
import { N8NWebhookPayload, N8NResponse } from "../types/module.types";
import { normalizeLocale } from "../../../constants/locales";
import {
  CampaignMetricsResponse,
  LeadsCardsResponse,
  GetLeadResponse,
  N8NActionResponse,
} from "../../../types/n8nResponses";
import {
  buildAddSellerParameters,
  type NewSellerIdentifiers,
} from "../../modules/CampaignCreator/utils/sellerPayload";
import type { ISRLeadUpdateInput, ISRNotesDocument, ISRUpdateDiff } from "../../../types";
import { buildISRUpdatePayload } from "../../../utils/isrPayload";
import { timingLog, nextExecNum, getExecCount } from "../../../utils/timingLog";

// TODO: Appwrite backend only

/**
 * Shape of n8n's credit-checker reply when the client lacks credits for an action.
 * n8n wraps it as `{ answer: { ... } }` (sometimes inside an array, which the proxy unwraps).
 */
export interface InsufficientCreditsAnswer {
  action_type?: string;
  mapped_price_key?: string;
  price_label?: string;
  total_items?: number;
  unit_price?: number;
  total_cost?: number;
  available_credits: number;
  remaining_credits?: number;
  has_enough_credits: false;
  credits_needed?: boolean;
  message: string;
}

/**
 * Thrown when n8n's credit checker rejects an action because the client lacks credits.
 * Carries the original `answer` payload so callers can show the message + Buy Credits CTA.
 */
export class InsufficientCreditsError extends Error {
  readonly answer: InsufficientCreditsAnswer;

  constructor(answer: InsufficientCreditsAnswer) {
    super(answer.message || "Insufficient credits");
    this.name = "InsufficientCreditsError";
    this.answer = answer;
  }
}

/**
 * Duck-type check for an InsufficientCreditsError. Prefer this over `instanceof`
 * across call sites: in the bundled/minified build, `instanceof` can fail when the
 * class identity is split across chunks, which would silently drop the Buy Credits
 * CTA. Checks by `name` + presence of the `answer` payload instead.
 */
export function isInsufficientCreditsError(
  error: unknown,
): error is InsufficientCreditsError {
  if (!error || typeof error !== "object") return false;
  const e = error as { name?: unknown; answer?: unknown };
  return (
    e.name === "InsufficientCreditsError" &&
    !!e.answer &&
    typeof e.answer === "object"
  );
}

/**
 * Thrown when N8N rejects a request with HTTP 401 — meaning the identity headers
 * (x-appwrite-user-id / x-appwrite-client-id) were missing, which happens when the
 * Appwrite session has expired. The caller must redirect to /login so the user
 * re-authenticates and the session cookie refreshes (the proxy re-injects the
 * headers on the next call from the fresh session).
 */
export class N8nAuthError extends Error {
  constructor(message = "Session expired — please sign in again.") {
    super(message);
    this.name = "N8nAuthError";
  }
}

/**
 * Duck-type check for an N8nAuthError (same rationale as isInsufficientCreditsError:
 * `instanceof` is unreliable across minified chunks).
 */
export function isN8nAuthError(error: unknown): error is N8nAuthError {
  if (!error || typeof error !== "object") return false;
  return (error as { name?: unknown }).name === "N8nAuthError";
}

/**
 * Build a clear "insufficient credits" message straight from the numbers n8n
 * already returns in the credit-checker reply (total_cost, available_credits,
 * remaining_credits). Falls back to n8n's own `message` only when those are absent.
 */
export function formatInsufficientCreditsMessage(
  answer: InsufficientCreditsAnswer,
): string {
  const need = answer.total_cost;
  const have = answer.available_credits ?? 0;

  // If n8n didn't include a cost, just use its own message verbatim.
  if (need === undefined || need === null) {
    return answer.message;
  }

  const shortfall = Math.max(0, need - have);
  const fmt = (n: number) =>
    Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");

  if (have <= 0) {
    return `Not enough credits — this action needs ${fmt(need)}. You have 0. Add ${fmt(shortfall)} credits to continue.`;
  }
  return `Not enough credits — this action needs ${fmt(need)}, you have ${fmt(have)}. Add ${fmt(shortfall)} more credits to continue.`;
}

/**
 * Returns true when a parsed n8n response is a credit-checker rejection.
 * Centralized so the same shape can be re-checked at any call site.
 *
 * n8n wraps the checker result as `{ answer: { has_enough_credits: false, ... } }`
 * (sometimes inside a 1-element array, which the proxy already unwraps). We also
 * tolerate the answer sitting at the top level, in case a workflow returns it
 * without the `answer` wrapper.
 */
export function isInsufficientCreditsResponse(
  data: unknown,
): data is { answer: InsufficientCreditsAnswer } {
  return extractInsufficientCreditsAnswer(data) !== null;
}

/**
 * Extract the credit-checker answer payload from a parsed n8n response, wherever
 * it sits (under `answer`, at the top level, or wrapped in a 1-element array whose
 * element may itself carry `answer`). Returns null when this isn't a credit rejection.
 */
export function extractInsufficientCreditsAnswer(
  data: unknown,
): InsufficientCreditsAnswer | null {
  if (!data || typeof data !== "object") return null;

  // Build the list of objects that could be the answer payload.
  const candidates: Record<string, unknown>[] = [];
  const collect = (value: unknown) => {
    if (!value || typeof value !== "object") return;
    const obj = value as Record<string, unknown>;
    candidates.push(obj);
    if (obj.answer && typeof obj.answer === "object") {
      candidates.push(obj.answer as Record<string, unknown>);
    }
    if (Array.isArray(obj) && obj.length === 1) {
      collect(obj[0]);
    }
  };
  collect(data);

  for (const candidate of candidates) {
    if (
      candidate.has_enough_credits === false ||
      candidate.credits_needed === true
    ) {
      return candidate as unknown as InsufficientCreditsAnswer;
    }
  }
  return null;
}

export class N8NService {
  private appwriteFunctionId: string;
  private client: Client;
  private functions: Functions;

  constructor() {
    // Initialize Appwrite client
    this.client = new Client()
      .setEndpoint(import.meta.env.VITE_APPWRITE_ENDPOINT || "")
      .setProject(import.meta.env.VITE_APPWRITE_PROJECT_ID || "");

    this.functions = new Functions(this.client);
    this.appwriteFunctionId = "n8n-proxy";
  }

  /**
   * Validate Appwrite configuration
   */
  private validateConfig(): void {
    if (!this.appwriteFunctionId) {
      throw new Error("Appwrite N8N function ID not configured");
    }
  }

  /**
   * Register a new user (triggers N8N workflow)
   * Called when a new user signs up via email/password, Google OAuth, or LinkedIn OAuth
   */
  async registerUser(data: {
    user_id: string;
    user_email: string;
    user_name?: string;
    auth_provider: "email" | "google" | "linkedin";
    company_name?: string;
    company_website?: string;
    phone_number?: string;
    job_title?: string;
    preferred_language?: string;
    team_id?: string;
    country_code?: string;
    state_name?: string;
  }): Promise<N8NResponse> {
    return this.sendWebhook({
      endpoint: "", // Not used - single webhook endpoint
      method: "POST",
      data: {
        action: "new_client", // Correct action for new client creation
        user_email: data.user_email,
        parameters: {
          user_id: data.user_id,
          user_email: data.user_email,
          user_name: data.user_name || data.user_email,
          client_name: data.company_name || data.user_name || data.user_email,
          client_website: data.company_website || undefined,
          auth_provider: data.auth_provider,
          company_name: data.company_name || undefined,
          phone_number: data.phone_number || undefined,
          job_title: data.job_title || undefined,
          preferred_language: data.preferred_language ? normalizeLocale(data.preferred_language) : undefined,
          team_id: data.team_id || undefined,
          country_code: data.country_code || undefined,
          state_name: data.state_name || undefined,
          timestamp: new Date().toISOString(),
          source: "lead-intelligence-app",
        },
      },
    });
  }

  /**
   * Read-only N8N actions — safe to retry. Self-hosted Appwrite occasionally
   * returns a 200 execution with an EMPTY body (phantom execution); retrying
   * these once turns a transient flake into a ~1s hiccup. Write actions are
   * deliberately excluded: a phantom may still have executed the write, and
   * a blind retry could duplicate a campaign/invitation.
   */
  private static readonly RETRYABLE_READ_ACTIONS = new Set([
    "campaign_metrics",
    "leads_cards",
    "get_lead",
    "get_team_roster",
    "get_campaign_metadata",
  ]);

  /**
   * Trigger Nexus personal AI server provisioning (triggers N8N workflow).
   *
   * 🔐 SECURITY: do NOT send user_email from the client ("never trust the client").
   * The n8n-proxy resolves the caller's email server-side from the Appwrite session
   * (x-appwrite-user-* headers / JWT) and forwards it securely to N8N.
   */
  async setupNexus(): Promise<N8NResponse> {
    return this.sendWebhook({
      endpoint: "",
      method: "POST",
      data: {
        action: "nexus_setup",
        parameters: {},
      },
    });
  }

  /**
   * Send webhook to N8N via Appwrite secure proxy
   * 🔐 SECURE: Calls Appwrite proxy function with authentication via Appwrite SDK
   */
  async sendWebhook(payload: N8NWebhookPayload): Promise<N8NResponse> {
    // Phantom-execution recovery for READ-ONLY actions: self-hosted Appwrite
    // sometimes returns a 200 execution with an empty body. Retrying an
    // idempotent read once is safe; write actions never get retried here
    // (a phantom may still have executed the write — retrying would duplicate it).
    // The same retry also covers timeouts: the proxy now aborts N8N calls at
    // 25s with a typed timeout error (before Appwrite's opaque 30s 408 wall),
    // so a slow N8N costs one fast retry instead of a full failed round-trip.
    const isRetryableRead = N8NService.RETRYABLE_READ_ACTIONS.has(payload.data.action || "");
    try {
      return await this.executeWebhook(payload);
    } catch (error: any) {
      // Session expired: N8N rejected the request with 401 (identity headers missing).
      // Redirect to /login so the user re-authenticates and the session cookie refreshes;
      // the proxy re-injects x-appwrite-user-id / x-appwrite-client-id on the next call.
      if (isN8nAuthError(error)) {
        console.warn("🔐 N8N returned 401 (session expired) — redirecting to /login");
        if (typeof window !== "undefined") {
          window.location.href = "/login";
        }
        // Re-throw so the caller's promise rejects (the redirect is already in flight).
        throw error;
      }
      const isEmptyBodyRetry = isRetryableRead &&
        error?.message?.includes("No response body from Appwrite function");
      const isTimeoutRetry = isRetryableRead && (
        error?.message?.includes("timed out") ||
        error?.message?.includes("Request Timeout") ||
        error?.message?.includes("timeout")
      );
      if (isEmptyBodyRetry || isTimeoutRetry) {
        console.warn("🔁 Retrying read action once:", payload.data.action, isEmptyBodyRetry ? "(empty body)" : "(timeout)");
        return this.executeWebhook(payload);
      }
      throw error;
    }
  }

  private async executeWebhook(payload: N8NWebhookPayload): Promise<N8NResponse> {
    const action = payload.data.action || 'unknown';
    const execNum = nextExecNum();
    const t0 = performance.now();
    timingLog('n8n', `createExecution #${execNum} call`, { action, function: this.appwriteFunctionId });
    try {
      this.validateConfig();

      console.log(`📤 Sending N8N webhook via Appwrite proxy`);
      console.log("Action:", payload.data.action || "N/A");

      // Build request body
      const requestBody = {
        action: payload.data.action,
        user_email: payload.data.user_email,
        parameters: payload.data.parameters || {},
      };

      console.log("📤 Request action:", requestBody.action);

      // Use Appwrite SDK to execute function with data parameter
      // The SDK properly formats the request with the data in the body
      const execution = await this.functions.createExecution(
        this.appwriteFunctionId,
        JSON.stringify(requestBody), // Data parameter - sent as string
        false, // Not async
        "/", // Path
        "POST", // Method
      );

      console.log("📦 Appwrite execution status:", execution?.responseStatusCode);

      // Timing: execution round-trip complete. Includes n8n-proxy cold start +
      // N8N workflow execution — the most variable leg of any data fetch.
      timingLog('n8n', `createExecution #${execNum} response`, {
        action,
        status: execution?.responseStatusCode,
        durationMs: performance.now() - t0,
        bodyBytes: execution?.responseBody?.length ?? 0,
        totalCallsSoFar: getExecCount(),
      });

      // N8N returned 401 = identity headers (x-appwrite-user-id / x-appwrite-client-id)
      // were missing → the Appwrite session has expired. Force a re-auth so the session
      // cookie refreshes and the proxy re-injects the headers on the next call.
      if (execution?.responseStatusCode === 401) {
        throw new N8nAuthError();
      }

      // Extract responseBody (contains actual N8N data as JSON string)
      const responseBody = execution.responseBody;
      if (!responseBody) {
        throw new Error("No response body from Appwrite function");
      }

      // Parse the N8N response from responseBody string
      const n8nResponse = JSON.parse(responseBody);
      console.log("✅ N8N webhook success");

      // Detect credit-checker rejection: n8n returns { answer: { has_enough_credits: false, ... } }
      // even though the action did NOT execute. Surface as a typed error so callers can show
      // the message and open the Buy Credits CTA. Tolerate the answer being nested under `data`,
      // sitting at the top level, or wrapped in a 1-element array.
      const insufficientAnswer = extractInsufficientCreditsAnswer(n8nResponse?.data);
      if (insufficientAnswer) {
        console.warn("💳 N8N credit checker rejected action:", insufficientAnswer);
        throw new InsufficientCreditsError(insufficientAnswer);
      }

      // Check if N8N proxy returned success
      if (!n8nResponse.success) {
        throw new Error(n8nResponse.error || "N8N proxy returned failure");
      }

      return {
        success: true,
        data: n8nResponse.data,
        timestamp: n8nResponse.timestamp || new Date().toISOString(),
      };
    } catch (error: any) {
      console.error("❌ N8N webhook error:", error);
      timingLog('n8n', `createExecution #${execNum} error`, {
        action,
        durationMs: performance.now() - t0,
        error: error?.message,
      });
      // Preserve typed errors (N8nAuthError, InsufficientCreditsError) so callers and
      // sendWebhook can branch on them. Only generic errors get re-wrapped into a plain
      // Error. Use the duck-type checks so the typed error survives bundler class-identity splitting.
      if (isN8nAuthError(error) || isInsufficientCreditsError(error)) {
        throw error;
      }
      throw new Error(`N8N webhook failed: ${error.message || "Unknown error"}`);
    }
  }

  /**
   * Create a new campaign (triggers N8N workflow)
   */
  async createCampaign(data: {
    name: string;
    description: string;
    target_audience?: string;
    send_outreach_mode?: "manual" | "auto";
    seller_lvl1?: string | null;
    settings?: Record<string, any>;
    client_email: string;
    client_id?: string;
    client_name?: string;
    stripe_id?: string;
  }): Promise<N8NResponse> {
    const sendOutreachMode =
      data.send_outreach_mode || (data.settings?.send_outreach_mode === "auto" ? "auto" : "manual");

    return this.sendWebhook({
      endpoint: "", // Not used - single webhook endpoint
      method: "POST",
      data: {
        action: "createCampaign",
        user_email: data.client_email,
        parameters: {
          name: data.name,
          description: data.description,
          target_audience: data.target_audience,
          seller_lvl1: data.seller_lvl1 ?? null,
          send_outreach_mode: sendOutreachMode,
          send_outreach: sendOutreachMode === "auto",
          settings: data.settings,
          client_email: data.client_email,
          client_id: data.client_id || "",
          client_name: data.client_name || data.client_email,
          stripe_id: data.stripe_id || "",
          timestamp: new Date().toISOString(),
          source: "lead-intelligence-app",
        },
      },
    });
  }

  /**
   * Add a seller to the current client (triggers the N8N add_seller workflow).
   */
  async addSeller(data: {
    identifiers: NewSellerIdentifiers;
    client_email: string;
    client_id?: string;
    team_name: string;
  }): Promise<N8NResponse> {
    // add_seller is a slow workflow (ClickUp task creation + enrichment
    // kickoff) that frequently hits the self-hosted Appwrite 30s sync wall.
    // sendWebhook excludes writes from its retry; addSeller is effectively
    // idempotent for our purposes (the team-pref dedup guard prevents
    // duplicate registry entries, and a retried ClickUp task creation is
    // tolerable), so retry once on the known transient failures to avoid
    // surfacing a false "failed" that drives users to re-submit (and create
    // the duplicate ClickUp tasks seen in prod).
    const send = () => this.sendWebhook({
      endpoint: "",
      method: "POST",
      data: {
        action: "add_seller",
        user_email: data.client_email,
        parameters: buildAddSellerParameters({
          newSeller: data.identifiers,
          clientEmail: data.client_email,
          clientId: data.client_id,
          teamName: data.team_name,
        }),
      },
    });

    try {
      let response: N8NResponse;
      try {
        response = await send();
      } catch (error: any) {
        const transient =
          error?.message?.includes("No response body from Appwrite function") ||
          error?.message?.includes("timed out") ||
          error?.message?.includes("timeout") ||
          error?.message?.includes("Request Timeout");
        if (!transient) throw error;
        console.warn("🔁 Retrying add_seller once:", error.message);
        response = await send();
      }
      return response;
    } catch (error: any) {
      console.error("❌ Error adding seller:", error);
      throw error;
    }
  }

  /**
   * Fetch a seller's enriched lvl1 profile (company + brand guidelines) from
   * N8N. Action: get_lvl1_profile. The n8n-proxy injects the authenticated
   * Appwrite identity headers (x-appwrite-user-id / client id) on every
   * execution, so only the lvl1_id needs to travel in parameters.
   */
  async getLvl1Profile(data: {
    lvl1_id: string;
    client_email: string;
  }): Promise<N8NResponse> {
    return this.sendWebhook({
      endpoint: "",
      method: "POST",
      data: {
        action: "get_lvl1_profile",
        user_email: data.client_email,
        parameters: {
          lvl1_id: data.lvl1_id,
          timestamp: new Date().toISOString(),
          source: "lead-intelligence-app",
        },
      },
    });
  }

  /**
   * Bulk enrich leads (triggers N8N enrichment workflow)
   */
  async bulkEnrichLeads(data: {
    campaign_id: string;
    lead_ids: string[];
    client_email: string;
    client_id?: string;
    client_name?: string;
    action: "enrich";
  }): Promise<N8NResponse> {
    return this.sendWebhook({
      endpoint: "", // Not used - single webhook endpoint
      method: "POST",
      data: {
        action: "enrich",
        user_email: data.client_email,
        parameters: {
          campaign_id: data.campaign_id,
          lead_ids: data.lead_ids,
          client_email: data.client_email,
          client_id: data.client_id || "",
          client_name: data.client_name || data.client_email,
          timestamp: new Date().toISOString(),
          source: "lead-intelligence-app",
          lead_count: data.lead_ids.length,
        },
      },
    });
  }

  /**
   * Re-enrich an already-enriched lead (updates contact data, report, ICP score)
   */
  async enrichUpdateLeads(data: {
    campaign_id: string;
    lead_ids: string[];
    client_email: string;
    client_id?: string;
    client_name?: string;
  }): Promise<N8NResponse> {
    timingLog('enrich', 'enrichUpdateLeads request', {
      campaign: data.campaign_id,
      leads: data.lead_ids.length,
    });
    const t0 = performance.now();
    try {
      const result = await this.sendWebhook({
        endpoint: "",
        method: "POST",
        data: {
          action: "enrich_update",
          user_email: data.client_email,
          parameters: {
            campaign_id: data.campaign_id,
            lead_ids: data.lead_ids,
            client_email: data.client_email,
            client_id: data.client_id || "",
            client_name: data.client_name || data.client_email,
            timestamp: new Date().toISOString(),
            source: "lead-intelligence-app",
            lead_count: data.lead_ids.length,
          },
        },
      });
      timingLog('enrich', 'enrichUpdateLeads response', {
        campaign: data.campaign_id,
        durationMs: performance.now() - t0,
        success: result.success,
      });
      return result;
    } catch (error) {
      timingLog('enrich', 'enrichUpdateLeads error', {
        campaign: data.campaign_id,
        durationMs: performance.now() - t0,
        error: (error as Error)?.message,
      });
      throw error;
    }
  }

  /**
   * Send leads to outreach (triggers N8N cold outreach workflow)
   */
  async sendToOutreach(data: {
    campaign_id: string;
    lead_ids: string[];
    outreach_type?: "email" | "linkedin" | "both";
    client_email: string;
    client_id?: string;
    client_name?: string;
    action: "outreach";
  }): Promise<N8NResponse> {
    // Build parameters object without undefined fields
    const parameters: any = {
      campaign_id: data.campaign_id,
      lead_ids: data.lead_ids,
      client_email: data.client_email,
      client_id: data.client_id || "",
      client_name: data.client_name || data.client_email,
      timestamp: new Date().toISOString(),
      source: "lead-intelligence-app",
      lead_count: data.lead_ids.length,
    };

    // Only include outreach_type if it's provided
    if (data.outreach_type) {
      parameters.outreach_type = data.outreach_type;
    }

    timingLog('outreach', 'sendToOutreach request', {
      campaign: data.campaign_id,
      leads: data.lead_ids.length,
      type: data.outreach_type || 'both',
    });
    const t0 = performance.now();
    try {
      const result = await this.sendWebhook({
        endpoint: "", // Not used - single webhook endpoint
        method: "POST",
        data: {
          action: "outreach",
          user_email: data.client_email,
          parameters,
        },
      });
      timingLog('outreach', 'sendToOutreach response', {
        campaign: data.campaign_id,
        durationMs: performance.now() - t0,
        success: result.success,
      });
      return result;
    } catch (error) {
      timingLog('outreach', 'sendToOutreach error', {
        campaign: data.campaign_id,
        durationMs: performance.now() - t0,
        error: (error as Error)?.message,
      });
      throw error;
    }
  }

  /**
   * Create a new campaign with CSV leads
   * Action: create_CSV_campaign
   * 🔥 UPDATED: Now accepts csv_storage_url (Appwrite URL) instead of raw csv_data
   */
  async createCSVCampaign(data: {
    name: string;
    primary_objective: string;
    goals: string;
    start_date: string;
    end_date: string;
    weekly_limit: string;
    outreach_channels: string[];
    send_outreach_mode?: "manual" | "auto";
    seller_lvl1?: string | null;
    csv_storage_url: string;
    csv_filename: string;
    client_email: string;
    client_id?: string;
    client_name?: string;
    stripe_id?: string;
  }): Promise<N8NResponse> {
    const sendOutreachMode = data.send_outreach_mode || "manual";

    return this.sendWebhook({
      endpoint: "", // Not used - single webhook endpoint
      method: "POST",
      data: {
        action: "create_CSV_campaign",
        user_email: data.client_email,
        parameters: {
          name: data.name,
          primary_objective: data.primary_objective,
          goals: data.goals,
          seller_lvl1: data.seller_lvl1 ?? null,
          start_date: data.start_date,
          end_date: data.end_date,
          weekly_limit: data.weekly_limit,
          outreach_channels: data.outreach_channels,
          send_outreach_mode: sendOutreachMode,
          send_outreach: sendOutreachMode === "auto",
          csv_storage_url: data.csv_storage_url,
          csv_filename: data.csv_filename,
          client_email: data.client_email,
          client_id: data.client_id || "",
          client_name: data.client_name || data.client_email,
          stripe_id: data.stripe_id || "",
          timestamp: new Date().toISOString(),
          source: "lead-intelligence-app",
        },
      },
    });
  }

  /**
   * Update existing campaign with CSV leads
   * Action: update_CSV_campaign
   * 🔥 UPDATED: Now accepts csv_storage_url Appwrite URL) instead of raw csv_data
   */
  async updateCSVCampaign(data: {
    campaign_id: string;
    csv_storage_url: string;
    csv_filename: string;
    client_email: string;
    client_id?: string;
    client_name?: string;
    stripe_id?: string;
  }): Promise<N8NResponse> {
    return this.sendWebhook({
      endpoint: "", // Not used - single webhook endpoint
      method: "POST",
      data: {
        action: "update_CSV_campaign",
        user_email: data.client_email,
        parameters: {
          campaign_id: data.campaign_id,
          csv_storage_url: data.csv_storage_url,
          csv_filename: data.csv_filename,
          client_email: data.client_email,
          client_id: data.client_id || "",
          client_name: data.client_name || data.client_email,
          stripe_id: data.stripe_id || "",
          timestamp: new Date().toISOString(),
          source: "lead-intelligence-app",
        },
      },
    });
  }

  /**
   * Bulk action on leads (generic method for enrich or outreach)
   */
  async bulkAction(data: {
    campaign_id: string;
    lead_ids: string[];
    client_email: string;
    client_id?: string;
    client_name?: string;
    action: "enrich" | "outreach";
  }): Promise<N8NResponse> {
    if (data.action === "enrich") {
      return this.bulkEnrichLeads({
        ...data,
        action: "enrich",
      });
    } else {
      return this.sendToOutreach({
        ...data,
        action: "outreach",
      });
    }
  }

  // ============================================================================
  // DATA FETCHING ACTIONS (N8N as Source of Truth)
  // ============================================================================

  /**
   * Get all campaigns with metrics for the logged user
   * Action: campaign_metrics
   *
   * Returns: All campaigns + details + metrics (total leads and leads by status)
   */
  async getCampaignMetrics(data: {
    client_email: string;
  }): Promise<N8NActionResponse<CampaignMetricsResponse>> {
    try {
      const response = await this.sendWebhook({
        endpoint: "",
        method: "POST",
        data: {
          action: "campaign_metrics",
          user_email: data.client_email,
          parameters: {
            client_email: data.client_email,
            timestamp: new Date().toISOString(),
            source: "lead-intelligence-app",
          },
        },
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to fetch campaign metrics");
      }

      return {
        success: true,
        data: response.data as CampaignMetricsResponse,
        timestamp: response.timestamp,
      };
    } catch (error: any) {
      console.error("❌ Error fetching campaign metrics:", error);
      return {
        success: false,
        error: error.message || "Unknown error",
        timestamp: new Date().toISOString(),
      };
    }
  }

  /**
   * Get all leads for a specific campaign (grid card view)
   * Action: leads_cards
   *
   * Returns: Key information for rendering lead grid cards
   * Note: icp_score is included at top level for default sorting
   */
  async getLeadsCards(data: {
    campaign_id: string;
    client_email: string;
  }): Promise<N8NActionResponse<LeadsCardsResponse>> {
    try {
      const response = await this.sendWebhook({
        endpoint: "",
        method: "POST",
        data: {
          action: "leads_cards",
          user_email: data.client_email,
          parameters: {
            campaign_id: data.campaign_id,
            client_email: data.client_email,
            timestamp: new Date().toISOString(),
            source: "lead-intelligence-app",
          },
        },
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to fetch leads cards");
      }

      return {
        success: true,
        data: response.data as LeadsCardsResponse,
        timestamp: response.timestamp,
      };
    } catch (error: any) {
      console.error("❌ Error fetching leads cards:", error);
      return {
        success: false,
        error: error.message || "Unknown error",
        timestamp: new Date().toISOString(),
      };
    }
  }

  /**
   * Get full lead data for rendering all tabs
   * Action: get_lead
   *
   * Returns: Complete lead data (lvl2 + lvl1_person) with all intelligence and outreach data
   */
  async getLead(data: {
    lead_id: string;
    campaign_id: string;
    client_email: string;
  }): Promise<N8NActionResponse<GetLeadResponse>> {
    timingLog('init', 'getLead request', { campaign: data.campaign_id, lead: data.lead_id });
    const t0 = performance.now();
    try {
      const response = await this.sendWebhook({
        endpoint: "",
        method: "POST",
        data: {
          action: "get_lead",
          user_email: data.client_email,
          parameters: {
            lead_id: data.lead_id,
            campaign_id: data.campaign_id,
            client_email: data.client_email,
            timestamp: new Date().toISOString(),
            source: "lead-intelligence-app",
          },
        },
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to fetch lead data");
      }

      timingLog('init', 'getLead response', {
        campaign: data.campaign_id,
        lead: data.lead_id,
        durationMs: performance.now() - t0,
        success: response.success,
      });
      return {
        success: true,
        data: response.data as GetLeadResponse,
        timestamp: response.timestamp,
      };
    } catch (error: any) {
      console.error("❌ Error fetching lead data:", error);
      timingLog('init', 'getLead error', {
        campaign: data.campaign_id,
        lead: data.lead_id,
        durationMs: performance.now() - t0,
        error: error?.message,
      });
      return {
        success: false,
        error: error.message || "Unknown error",
        timestamp: new Date().toISOString(),
      };
    }
  }

  /**
   * Persist the complete ISR-editable lead snapshot through n8n.
   * The notes document is always sent in full; this method never sends note deltas.
   */
  async updateISRLead(
    data: ISRLeadUpdateInput & { client_email: string },
  ): Promise<N8NActionResponse<any>> {
    try {
      const parameters = buildISRUpdatePayload(data);
      const response = await this.sendWebhook({
        endpoint: "",
        method: "POST",
        data: {
          action: "update_isr_lead",
          user_email: data.client_email,
          parameters,
        },
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to update SDR lead");
      }

      return {
        success: true,
        data: response.data,
        timestamp: response.timestamp,
      };
    } catch (error: any) {
      // Deliberately omit the submitted snapshot: it contains contact PII and notes.
      console.error("❌ Error updating ISR lead");
      return {
        success: false,
        error: error.message || "Unknown error",
        timestamp: new Date().toISOString(),
      };
    }
  }

  /**
   * Update campaign fields through a single n8n action.
   * Action: update_campaign
   */
  async updateCampaign(data: {
    campaign_id: string;
    client_email: string;
    client_id?: string;
    client_name?: string;
    status?: "active" | "paused" | "archived" | "delete";
    weekly_limit?: number;
    campaign_name?: string;
    weekly_reset?: string;
    resources_assigned?: Record<string, unknown>;
    start_date?: string | null;
    end_date?: string | null;
    lvl2_id?: string;
    lead_status?: string;
    lead_sub_status?: string;
    /** Complete activity document: existing entries plus the new status note. */
    notes?: ISRNotesDocument;
    icp_score?: number;
    /** Rich structured diff with new/previous values per category.
     *  personLvl2 fields are applied directly; company fields are suggestions only
     *  (shared lvl1 records require backend approval). */
    updated?: ISRUpdateDiff;
    /** Canonical lvl1 record IDs — let the workflow target the exact person /
     *  company rows without re-resolving them from lvl2 cross-references. */
    person_lvl1_id?: string;
    company_lvl1_id?: string;
    /** Routing hint derived from the change categories (contact_details |
     *  lead_status | notes | snapshot) so the workflow can branch to the right
     *  sub-path without re-deriving it from the diff. */
    sub_action?: string;
  }): Promise<N8NActionResponse<any>> {
    timingLog('mutation', 'updateCampaign request', {
      campaign: data.campaign_id,
      subAction: data.sub_action || 'none',
      leadStatus: data.lead_status || 'none',
      hasNotes: data.notes !== undefined,
      hasUpdated: data.updated !== undefined,
    });
    const t0 = performance.now();
    try {
      const response = await this.sendWebhook({
        endpoint: "",
        method: "POST",
        data: {
          action: "update_campaign",
          user_email: data.client_email,
          parameters: {
            campaign_id: data.campaign_id,
            ...(data.status !== undefined ? { status: data.status } : {}),
            ...(data.weekly_limit !== undefined ? { weekly_limit: data.weekly_limit } : {}),
            ...(data.campaign_name !== undefined ? { campaign_name: data.campaign_name } : {}),
            ...(data.weekly_reset !== undefined ? { weekly_reset: data.weekly_reset } : {}),
            ...(data.resources_assigned !== undefined ? { resources_assigned: data.resources_assigned } : {}),
            ...(data.start_date !== undefined ? { start_date: data.start_date } : {}),
            ...(data.end_date !== undefined ? { end_date: data.end_date } : {}),
            ...(data.lvl2_id ? { lvl2_id: data.lvl2_id } : {}),
            ...(data.lead_status ? { lead_status: data.lead_status } : {}),
            ...(data.lead_sub_status !== undefined ? { lead_sub_status: data.lead_sub_status } : {}),
            ...(data.notes !== undefined ? { notes: data.notes } : {}),
            ...(data.icp_score !== undefined ? { icp_score: data.icp_score } : {}),
            ...(data.updated !== undefined ? { updated: data.updated } : {}),
            ...(data.person_lvl1_id ? { person_lvl1_id: data.person_lvl1_id } : {}),
            ...(data.company_lvl1_id ? { company_lvl1_id: data.company_lvl1_id } : {}),
            ...(data.sub_action ? { sub_action: data.sub_action } : {}),
            client_email: data.client_email,
            client_id: data.client_id || "",
            client_name: data.client_name || data.client_email,
            timestamp: new Date().toISOString(),
            source: "lead-intelligence-app",
          },
        },
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to update campaign");
      }

      timingLog('mutation', 'updateCampaign response', {
        campaign: data.campaign_id,
        subAction: data.sub_action || 'none',
        durationMs: performance.now() - t0,
        success: response.success,
      });
      return {
        success: true,
        data: response.data,
        timestamp: response.timestamp,
      };
    } catch (error: any) {
      console.error("❌ Error updating campaign:", error);
      timingLog('mutation', 'updateCampaign error', {
        campaign: data.campaign_id,
        subAction: data.sub_action || 'none',
        durationMs: performance.now() - t0,
        error: error?.message,
      });
      return {
        success: false,
        error: error.message || "Unknown error",
        timestamp: new Date().toISOString(),
      };
    }
  }

  /**
   * Backwards-compatible wrapper for weekly limit updates.
   */
  async updateCampaignWeeklyLimit(data: {
    campaign_id: string;
    weekly_limit: number;
    client_email: string;
    client_id?: string;
    client_name?: string;
  }): Promise<N8NActionResponse<any>> {
    return this.updateCampaign(data);
  }

  /**
   * Backwards-compatible wrapper for status updates.
   */
  async updateCampaignStatus(data: {
    campaign_id: string;
    status: "active" | "paused" | "archived" | "delete";
    client_email: string;
    client_id?: string;
    client_name?: string;
  }): Promise<N8NActionResponse<any>> {
    return this.updateCampaign(data);
  }

  // ============================================================================
  // TEAM ADMIN ACTIONS
  // ============================================================================

  /**
   * Reassign the account manager for a campaign in ClickUp.
   * Action: update_account_manager
   */
  async updateAccountManager(data: {
    campaign_id: string;
    account_manager_id: string;   // Appwrite user.$id of new manager
    account_manager_email: string;
    admin_email: string;
  }): Promise<N8NActionResponse<{ success: boolean }>> {
    // sendWebhook excludes WRITE actions from its phantom/timeout retry (a
    // blind retry could duplicate a create). update_account_manager is
    // idempotent though — assigning the same manager twice changes nothing —
    // so one manual retry on the known transient failures is safe. Without
    // it, a phantom execution (empty 200) surfaces as "first click reverts,
    // second click works" in Team view.
    const send = () => this.sendWebhook({
      endpoint: "",
      method: "POST",
      data: {
        action: "update_account_manager",
        user_email: data.admin_email,
        parameters: {
          campaign_id: data.campaign_id,
          account_manager_id: data.account_manager_id,
          account_manager_email: data.account_manager_email,
          timestamp: new Date().toISOString(),
          source: "lead-intelligence-app",
        },
      },
    });

    try {
      let response: N8NResponse;
      try {
        response = await send();
      } catch (error: any) {
        const transient =
          error?.message?.includes("No response body from Appwrite function") ||
          error?.message?.includes("timeout") ||
          error?.message?.includes("timed out");
        if (!transient) throw error;
        console.warn("🔁 Retrying idempotent update_account_manager once:", error.message);
        response = await send();
      }

      if (!response.success) {
        throw new Error(response.error || "Failed to update account manager");
      }

      return { success: true, data: { success: true }, timestamp: response.timestamp };
    } catch (error: any) {
      console.error("❌ Error updating account manager:", error);
      return { success: false, error: error.message, timestamp: new Date().toISOString() };
    }
  }

  /**
   * Invite a new user to the admin's Appwrite team.
   * The n8n-proxy Appwrite Function handles creating/inviting the user and adding them to the team.
   * Action: invite_team_member
   */
  async inviteTeamMember(data: {
    invite_email: string;
    invite_name?: string;
    team_id: string;
    roles?: string[];
    admin_email: string;
  }): Promise<N8NActionResponse<{ success: boolean }>> {
    try {
      const response = await this.sendWebhook({
        endpoint: "",
        method: "POST",
        data: {
          action: "invite_team_member",
          user_email: data.admin_email,
          parameters: {
            invite_email: data.invite_email,
            invite_name: data.invite_name || data.invite_email,
            team_id: data.team_id,
            roles: data.roles ?? ["member"],
            timestamp: new Date().toISOString(),
            source: "lead-intelligence-app",
          },
        },
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to invite team member");
      }

      return { success: true, data: { success: true }, timestamp: response.timestamp };
    } catch (error: any) {
      console.error("❌ Error inviting team member:", error);
      return { success: false, error: error.message, timestamp: new Date().toISOString() };
    }
  }

  /**
   * Cancel a pending invitation from the admin's Appwrite team.
   * Action: cancel_team_invitation
   */
  async cancelTeamInvitation(data: {
    team_id: string;
    membership_id: string;
    admin_email: string;
  }): Promise<N8NActionResponse<{ success: boolean }>> {
    try {
      const response = await this.sendWebhook({
        endpoint: "",
        method: "POST",
        data: {
          action: "cancel_team_invitation",
          user_email: data.admin_email,
          parameters: {
            team_id: data.team_id,
            membership_id: data.membership_id,
            timestamp: new Date().toISOString(),
            source: "lead-intelligence-app",
          },
        },
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to cancel invitation");
      }

      return { success: true, data: { success: true }, timestamp: response.timestamp };
    } catch (error: any) {
      console.error("❌ Error cancelling team invitation:", error);
      return { success: false, error: error.message, timestamp: new Date().toISOString() };
    }
  }

  /**
   * Remove a user from the admin's Appwrite team.
   * Also reassigns all their campaigns to the calling admin.
   * Action: remove_team_member
   */
  async removeTeamMember(data: {
    remove_user_id: string;
    remove_user_email: string;
    team_id: string;
    membership_id: string;
    admin_email: string;
    admin_user_id: string;
  }): Promise<N8NActionResponse<{ success: boolean }>> {
    try {
      const response = await this.sendWebhook({
        endpoint: "",
        method: "POST",
        data: {
          action: "remove_team_member",
          user_email: data.admin_email,
          parameters: {
            remove_user_id: data.remove_user_id,
            remove_user_email: data.remove_user_email,
            team_id: data.team_id,
            membership_id: data.membership_id,
            admin_user_id: data.admin_user_id,
            timestamp: new Date().toISOString(),
            source: "lead-intelligence-app",
          },
        },
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to remove team member");
      }

      return { success: true, data: { success: true }, timestamp: response.timestamp };
    } catch (error: any) {
      console.error("❌ Error removing team member:", error);
      return { success: false, error: error.message, timestamp: new Date().toISOString() };
    }
  }
}

export const n8nService = new N8NService();
